/**
 * Admin Assessment DTOs - Matching backend AdminAssessment DTOs
 * These DTOs represent assessment definitions and requests for backend integration
 */

/**
 * AdminPreLoad DTO - Represents preload configuration data.
 * Wire/C# shape is {FieldCode, Type, Value, LanguageTypeId} (same AdminPreLoadDto class used
 * for segment/account preloads elsewhere) — normalized to this camelCase shape by
 * AdminAssessmentStorage.normalizeAssessment. `name` (not `fieldCode`) is kept for backward
 * compatibility with existing getPreload(name) report-script call sites.
 */
export interface AdminPreLoadDto {
  name: string;
  value: string;
  type?: string;
  languageTypeId?: string;
}

/**
 * AdminReportSection DTO - Individual report section definition.
 * Preloads used to live here (reportSectionPreloads) — moved up to the report level
 * (AdminAssessmentReportDto.reportPreloads) since the backend now scopes them to the whole
 * report, not an individual section.
 */
export interface AdminReportSectionDto {
  dName: string;
  reportSectionId: string;
  reportScript: string;
}

/**
 * AdminSubReport DTO - Sub-report with multiple sections
 */
export interface AdminSubReportDto {
  dName: string;
  reportSections: AdminReportSectionDto[];
}

/**
 * AdminAssessmentReport DTO - Report definition for assessments
 */
export interface AdminAssessmentReportDto {
  /** Stable backend identifier for this report — the correct value to key report selection
   * off of (e.g. AdminView's renderReport PreScript flag, ShareReportGetResponseDto.ReportId).
   * dName is a display label and may not be unique/stable; prefer reportId wherever available. */
  reportId: string;
  dName: string;
  targetType: string;
  subReports: AdminSubReportDto[];
  /** Preloads scoped to the whole report — readable via getPreload(name) from any of this
   * report's section scripts (see reportRenderer.ts). Moved here from AdminReportSectionDto.
   *
   * A few names are also read by ReportView itself (not just section scripts) to turn a report
   * into a "hub" / TestDrive container — see getReportHubConfig in reportRenderer.ts:
   *   hubReport            truthy → render this report with child nav buttons + a gated continue button
   *   hubChildReports      "<reportId>:<label>" or "<reportId>:<label>:<instruction>" triples,
   *                        comma-separated (e.g. "6:View Client Report:Goes to your client,5:View Clinician Report")
   *   hubRequireAllViewed  "0" to let the continue button work without opening every child (default: require)
   *   hubContinueLabel     label for the hub's continue button (default "Next")
   *   hubFooter            centered message shown on the hub screen just above the continue button */
  reportPreloads: AdminPreLoadDto[];
}

/**
 * A single data variable this assessment exposes for Stats > Aggregate report items to source
 * from (AggReportSetupView's "Variable"/"Group By" dropdowns). Wire/C# shape is {DName, MVar} —
 * normalized to this camelCase shape by AdminAssessmentStorage.normalizeAssessment, same as
 * AdminPreLoadDto above.
 */
export interface StatsAggVarDto {
  dName: string;
  mVar: string;
}

/**
 * Complete AdminAssessment DTO with hierarchical structure including reports
 */
export interface AdminAssessmentDto {
  dName: string;
  surveybList: string;
  surveyId: string; // Links with AdminResponseDto.recSurvey
  sourceId: string;
  languageTypeId: string;
  extra: string;
  reports: AdminAssessmentReportDto[];
  /** Data variables this assessment exposes for Stats > Aggregate reports to source from. */
  aggVars: StatsAggVarDto[];
}

/**
 * Complete AdminAssessmentList DTO with hierarchical structure
 */
export interface AdminAssessmentListDto {
  assessments: AdminAssessmentDto[];
}

/**
 * Complete AdminRequest DTO with hierarchical structure
 */
export interface AdminRequestDto {
  launchqueueId: string;
  launchqueueData: string;
  profileId: string;
  dueDate: string; // ISO string format
  terminate: string; // ISO string format
  status: string;
  assessment: AdminAssessmentDto;
}

/**
 * Complete AdminRequestList DTO with hierarchical structure
 */
export interface AdminRequestListDto {
  adminRequests: AdminRequestDto[];
}

/**
 * Complete AdminLaunchRequest DTO with hierarchical structure
 */
export interface AdminLaunchRequestDto {
  launchqueueId: string;
  launchqueueData: string;
  profileId: string;
  dueDate: string; // ISO string format
  terminate: string; // ISO string format
  status: string;
  assessment: AdminAssessmentDto;
  preloadOverrides: AdminPreLoadDto[];
  reportPreloadOverrides: AdminPreLoadDto[];
}

/**
 * Complete AdminLaunchRequestList DTO with hierarchical structure
 */
export interface AdminLaunchRequestListDto {
  adminRequests: AdminLaunchRequestDto[];
}

/**
 * Helper functions for AdminAssessment operations
 */

/**
 * Create a new AdminPreLoadDto
 */
export const createAdminPreLoadDto = (
  name: string,
  value: string,
  type?: string,
  languageTypeId?: string
): AdminPreLoadDto => ({
  name,
  value,
  type,
  languageTypeId
});

/**
 * Create a new AdminReportSectionDto
 */
export const createAdminReportSectionDto = (
  dName: string,
  reportSectionId: string,
  reportScript: string
): AdminReportSectionDto => ({
  dName,
  reportSectionId,
  reportScript
});

/**
 * Create a new AdminSubReportDto
 */
export const createAdminSubReportDto = (
  dName: string,
  reportSections: AdminReportSectionDto[] = []
): AdminSubReportDto => ({
  dName,
  reportSections
});

/**
 * Create a new AdminAssessmentReportDto
 */
export const createAdminAssessmentReportDto = (
  reportId: string,
  dName: string,
  targetType: string,
  subReports: AdminSubReportDto[] = [],
  reportPreloads: AdminPreLoadDto[] = []
): AdminAssessmentReportDto => ({
  reportId,
  dName,
  targetType,
  subReports,
  reportPreloads
});

/**
 * Create a new AdminAssessmentDto
 */
export const createAdminAssessmentDto = (
  dName: string,
  surveybList: string,
  surveyId: string = 'ASSESSMENT', // Links with AdminResponseDto.recSurvey
  sourceId: string,
  languageTypeId: string = '',
  extra: string = '',
  reports: AdminAssessmentReportDto[] = [],
  aggVars: StatsAggVarDto[] = []
): AdminAssessmentDto => ({
  dName,
  surveybList,
  surveyId,
  sourceId,
  languageTypeId,
  extra,
  reports,
  aggVars
});

/**
 * Create a new AdminRequestDto
 */
export const createAdminRequestDto = (
  launchqueueId: string,
  profileId: string,
  assessment: AdminAssessmentDto,
  status: string = 'PENDING',
  launchqueueData: string = '',
  dueDate?: string,
  terminate?: string
): AdminRequestDto => ({
  launchqueueId,
  launchqueueData,
  profileId,
  dueDate: dueDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // Default 7 days from now
  terminate: terminate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // Default 30 days from now
  status,
  assessment
});

/**
 * Create empty assessment list - assessments should now be downloaded as AdminAssessment content
 * Hardcoded assessments removed since real AdminAssessmentDto definitions can be downloaded
 */
export const createDefaultAssessmentList = (): AdminAssessmentListDto => ({
  assessments: [
    // No hardcoded assessments - download AdminAssessment content instead
  ]
});

/**
 * Assessment status constants
 */
export const ASSESSMENT_STATUS = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  EXPIRED: 'EXPIRED'
} as const;

/**
 * Assessment source type constants  
 */
export const ASSESSMENT_SOURCE_TYPE = {
  ASSESSMENT: 'ASSESSMENT',
  SCREENING: 'SCREENING',
  FOLLOWUP: 'FOLLOWUP'
} as const;

export type AssessmentStatus = typeof ASSESSMENT_STATUS[keyof typeof ASSESSMENT_STATUS];
export type AssessmentSourceType = typeof ASSESSMENT_SOURCE_TYPE[keyof typeof ASSESSMENT_SOURCE_TYPE];